import type { HttpClient } from '@angular/common/http';
import type { FormGroup } from '@angular/forms';
import { build, pool } from 'src/app/common/classes/request-builder';
import type { DropdownData, FilteredValue } from '@fitogest/reactive-form-builder/interfaces';
import type { ComunidadesRegantesModel } from 'src/app/models/analisis/comunidadesRegantes.model';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';

const EDIT_FORM_TYPE_LIMIT = 3;

export type ParcelasFormBuilderControlLike = {
    readonly value: unknown;
    readonly patchValue?: (value: unknown, options?: { readonly emitEvent?: boolean }) => void;
    readonly markAsPristine?: () => void;
};

export type ParcelasFormBuilderModelLike = {
    readonly get: (fieldName: string) => ParcelasFormBuilderControlLike | null;
};

export type ParcelasFormBuilderRefLike = {
    readonly model?: ParcelasFormBuilderModelLike | null;
};

export type ParcelasNewComunidadRegantesDefaults = {
    readonly userId: unknown;
    readonly clientId: unknown;
};

export type ParcelasNewComunidadRegantesFormValue = {
    readonly nombre: string;
    readonly id_usuario: unknown;
    readonly id_cliente: unknown;
};

export type ParcelasNewComunidadRegantesContext = ParcelasNewComunidadRegantesDefaults & {
    readonly comunidadForm: FormGroup;
    readonly endpoint: string;
    readonly http: HttpClient;
    readonly reloadRequest: RequestHandler<ComunidadesRegantesModel[]>;
    readonly comunidadesRegantesDropdown: DropdownData<ComunidadesRegantesModel>;
    readonly formBuilderRef?: ParcelasFormBuilderRefLike | null;
    readonly formType: number;
    readonly closeDialog: () => void;
    readonly refreshReactiveDropdownMappings: () => void;
};

export type ParcelasCreatedComunidadSelectionContext = {
    readonly comunidadesRegantesDropdown: DropdownData<ComunidadesRegantesModel>;
    readonly formBuilderRef?: ParcelasFormBuilderRefLike | null;
    readonly formType: number;
    readonly refreshReactiveDropdownMappings: () => void;
};

export type ParcelasCreatedComunidadSelectionResult = {
    readonly controlName: 'id_comunidad_regantes' | 'comunidad_regantes';
    readonly selectedId: unknown;
} | null;

/**
 * Handles new-option creation flows that still belong to the Parcelas legacy form shell.
 *
 * Keeping HTTP creation, legacy dropdown reload and post-create selection here avoids spreading mutable list
 * orchestration through the visual component while the field itself migrates to declarative data sources.
 */
export class ParcelasNewOptionAdapter {

    public static saveComunidadRegantes(context: ParcelasNewComunidadRegantesContext): void {
        if (context.comunidadForm.invalid) {
            context.comunidadForm.markAllAsTouched();
            return;
        }

        const payload = context.comunidadForm.getRawValue() as ComunidadesRegantesModel;

        context.http.post<ComunidadesRegantesModel>(context.endpoint, payload)
            .subscribe({
                next: () => {
                    context.comunidadForm.reset(this.buildDefaultComunidadFormValue(context));
                    context.closeDialog();

                    void this.reloadAndSelectComunidadRegantes(context, payload.nombre);
                },
                error: (error: unknown) => {
                    console.error('Error creating comunidad_regantes:', error);
                },
            });
    }

    public static buildDefaultComunidadFormValue(
        defaults: ParcelasNewComunidadRegantesDefaults,
    ): ParcelasNewComunidadRegantesFormValue {
        return {
            nombre: '',
            id_usuario: defaults.userId,
            id_cliente: defaults.clientId,
        };
    }

    public static selectCreatedComunidadRegantes(
        context: ParcelasCreatedComunidadSelectionContext,
        createdName: unknown,
    ): ParcelasCreatedComunidadSelectionResult {
        const match = this.findComunidadRegantesByName(context.comunidadesRegantesDropdown, createdName);
        const controlName = this.getComunidadRegantesControlName(context.formType);

        if (match) {
            const control = context.formBuilderRef?.model?.get(controlName);
            const selectedId = match.value?.id;

            if (typeof control?.patchValue === 'function') {
                control.patchValue(selectedId, { emitEvent: false });
                control.markAsPristine?.();
            }

            context.refreshReactiveDropdownMappings();

            return {
                controlName,
                selectedId,
            };
        }

        context.refreshReactiveDropdownMappings();

        return null;
    }

    private static reloadAndSelectComunidadRegantes(
        context: ParcelasNewComunidadRegantesContext,
        createdName: unknown,
    ): Promise<void> {
        return pool([
            build(context.reloadRequest, context.comunidadesRegantesDropdown, 'nombre'),
        ], () => {})
            .then(() => {
                this.selectCreatedComunidadRegantes(context, createdName);
                return;
            })
            .catch((error: unknown) => {
                console.error('Error reloading comunidades_regantes:', error);
            });
    }

    private static findComunidadRegantesByName(
        dropdown: DropdownData<ComunidadesRegantesModel>,
        createdName: unknown,
    ): FilteredValue<ComunidadesRegantesModel> | null {
        const targetName = this.toComparableName(createdName);

        return dropdown.filtered.find((item) => this.toComparableName(item.value?.nombre) === targetName) ?? null;
    }

    private static getComunidadRegantesControlName(formType: number): 'id_comunidad_regantes' | 'comunidad_regantes' {
        return formType < EDIT_FORM_TYPE_LIMIT ? 'id_comunidad_regantes' : 'comunidad_regantes';
    }

    private static toComparableName(value: unknown): string {
        return String(value ?? '').trim();
    }
}
